import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import functions from '../../../index'
import { PermissionPolicyOption } from '../../model/PermissionPolicyOption'
import { RoleRow } from '../../model/RoleRow'
import {
  OperationI18nLabelKey,
  ScopeVO,
} from '../../../../../domain/value-object/ScopeVO'

export const useAvailableFunctionsTooltipGetter = () => {
  const intl = useIntl()
  const availableFunctionsTitle = useMemo(
    () =>
      intl.formatMessage({ id: 'permissionPolicy.available.functions.title' }),
    [intl]
  )
  const noneLabel = useMemo(() => intl.formatMessage({ id: 'none' }), [intl])
  const getAvailableFunctionText = useCallback(
    (s: ScopeVO): string | undefined => {
      if (
        s.applicationFunction &&
        functions[s.applicationFunction.externalId] // feature-flagで非表示にされる機能を除くための条件
      ) {
        return `${s.applicationFunction.name} ${intl.formatMessage({ id: OperationI18nLabelKey[s.operation] })}`
      }

      return undefined
    },
    [intl]
  )

  const getAvailableFunctionsTooltipOfPermissionPolicy = useCallback(
    (permissionPolicyOption: PermissionPolicyOption) => {
      const tooltipLines: string[] = []
      if (permissionPolicyOption.description) {
        tooltipLines.push(permissionPolicyOption.description)
        tooltipLines.push('')
      }
      tooltipLines.push(availableFunctionsTitle)
      permissionPolicyOption.scopes.forEach(s => {
        const availableFunctionText = getAvailableFunctionText(s)
        if (availableFunctionText) {
          tooltipLines.push(availableFunctionText)
        }
      })

      return tooltipLines.join('\n')
    },
    [availableFunctionsTitle, getAvailableFunctionText]
  )
  const getAvailableFunctionsTooltipOfRole = useCallback(
    (role: RoleRow, permissionPolicyOptions: PermissionPolicyOption[]) => {
      const functionSet = new Set<string>()
      const selectedPolicies = permissionPolicyOptions.filter(p =>
        role.permissionPolicyUuids.includes(p.uuid)
      )

      selectedPolicies.forEach(policy => {
        policy.scopes.forEach(scope => {
          const availableFunctionText = getAvailableFunctionText(scope)
          if (availableFunctionText) {
            functionSet.add(availableFunctionText)
          }
        })
      })

      const availableFunctions = Array.from(functionSet)
      const tooltip = [availableFunctionsTitle]
      if (availableFunctions.length === 0) {
        tooltip.push(noneLabel)
      } else {
        tooltip.push(...availableFunctions)
      }
      return tooltip.join('\n')
    },
    [availableFunctionsTitle, getAvailableFunctionText, noneLabel]
  )

  return {
    getAvailableFunctionsTooltipOfPermissionPolicy,
    getAvailableFunctionsTooltipOfRole,
  }
}
